import { StepPosition } from '../value-objects/StepPosition';
import { StepType } from '../value-objects/StepType';

/**
 * OnboardingStep Entity Properties
 */
export interface OnboardingStepProps {
  id: string;
  title: string;
  description: string;
  targetSelector?: string;
  position: StepPosition;
  type: StepType;
  route?: string;
  requiredAction?: string;
  imageUrl?: string;
  videoUrl?: string;
  order: number;
}

/**
 * OnboardingStep Entity
 * Represents a single step in the onboarding tour
 *
 * Steps are defined in the presentation layer (onboarding.store.ts)
 * and converted to entities for business logic
 */
export class OnboardingStep {
  private readonly props: OnboardingStepProps;

  constructor(props: OnboardingStepProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get title(): string {
    return this.props.title;
  }

  get description(): string {
    return this.props.description;
  }

  get targetSelector(): string | undefined {
    return this.props.targetSelector;
  }

  get position(): StepPosition {
    return this.props.position;
  }

  get type(): StepType {
    return this.props.type;
  }

  get route(): string | undefined {
    return this.props.route;
  }

  get requiredAction(): string | undefined {
    return this.props.requiredAction;
  }

  get imageUrl(): string | undefined {
    return this.props.imageUrl;
  }

  get videoUrl(): string | undefined {
    return this.props.videoUrl;
  }

  get order(): number {
    return this.props.order;
  }

  /**
   * Check if step requires navigation to a specific route
   */
  requiresNavigation(): boolean {
    return this.props.route !== undefined;
  }

  /**
   * Check if step has a target element to highlight
   */
  hasTarget(): boolean {
    return this.props.targetSelector !== undefined;
  }

  /**
   * Check if step requires user action to proceed
   */
  requiresAction(): boolean {
    return this.props.requiredAction !== undefined;
  }

  /**
   * Check if step has media (image or video)
   */
  hasMedia(): boolean {
    return this.props.imageUrl !== undefined || this.props.videoUrl !== undefined;
  }

  /**
   * Check if step is a modal-style step
   */
  isModal(): boolean {
    return this.props.type === 'modal';
  }

  /**
   * Check if step is a tooltip-style step
   */
  isTooltip(): boolean {
    return this.props.type === 'tooltip';
  }

  /**
   * Check if step is a spotlight-style step
   */
  isSpotlight(): boolean {
    return this.props.type === 'spotlight';
  }

  /**
   * Check if step requires action
   */
  isActionStep(): boolean {
    return this.props.type === 'action';
  }

  /**
   * Get the position for this step
   */
  getPosition(): number {
    return this.props.order;
  }

  /**
   * Check if step is for a specific role
   * (This logic is handled at the store level, but included for completeness)
   */
  isForRole(_role: 'director' | 'teacher' | 'student'): boolean {
    // Steps are already filtered by role in the store
    // This method exists for future role-specific step logic
    return true;
  }

  /**
   * Convert to plain object for serialization
   */
  toPlainObject(): OnboardingStepProps {
    return { ...this.props };
  }
}
